import React, { useEffect, useState } from "react";
import _ from "lodash";
import Heading from "@amzn/meridian/heading";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Input from "@amzn/meridian/input";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Link from "@amzn/meridian/link";
import Text from "@amzn/meridian/text";

import { ImageSheet } from "../commons/ImageSheet";
import { formatInputWithPrefixAndSuffix } from "../../../utils/formattingUtils";

interface IIncomeRateTableProps {
    isEditing: boolean;
    heading: string;
    data: any;
    spaceId: string;
    handleUpdateData: any;
}

interface TableConfig {
    header: string;
    key: string;
    editable: boolean;
    prefix: string;
    suffix: string;
    imageObjectKey?: string;
}

const getDataType = (heading: string): string => {
    switch (heading.toLowerCase()) {
        case "market cap rate":
            return "marketCapRate";
        case "vacancy":
            return "vacancy";
        case "rental rate":
            return "rentalRate";
        default:
            return "";
    }
};

const getTableConfig = (dataType: string): TableConfig[] => {
    const commonFields = [
        {
            header: "Source No",
            key: "sourceNo",
            editable: false,
            prefix: "",
            suffix: "",
        },
        {
            header: "Source Name",
            key: "sourceName",
            editable: false,
            prefix: "",
            suffix: "",
            imageObjectKey: "sourceNameS3Object",
        },
    ];

    switch (dataType) {
        case "marketCapRate":
            return [
                ...commonFields,
                {
                    header: "Cap rate in CBRE",
                    key: "capRateInCBRE",
                    editable: false,
                    prefix: "",
                    suffix: "%",
                },
                {
                    header: "Cap rate (unloaded)",
                    key: "capRateUnLoaded",
                    editable: true,
                    prefix: "",
                    suffix: "%",
                },
            ];
        case "vacancy":
            return [
                ...commonFields,
                {
                    header: "Vacancy in CBRE",
                    key: "vacancyInCBRE",
                    editable: false,
                    prefix: "",
                    suffix: "%",
                },
                {
                    header: "Vacancy",
                    key: "vacancy",
                    editable: true,
                    prefix: "",
                    suffix: "%",
                },
            ];
        case "rentalRate":
            return [
                ...commonFields,
                {
                    header: "Size(SqFt)",
                    key: "sizeSqFt",
                    editable: false,
                    prefix: "",
                    suffix: "",
                },
                {
                    header: "Annual Rent/SqFt",
                    key: "annualRentPerSqFt",
                    editable: false,
                    prefix: "$",
                    suffix: "",
                },
            ];
        default:
            return [];
    }
};

export const IncomeRateTable: React.FC<IIncomeRateTableProps> = ({
    isEditing,
    heading,
    data,
    spaceId,
    handleUpdateData,
}) => {
    const [tableData, setTableData] = useState([]);
    const [reconciledValue, setReconciledValue] = useState("");
    const [displayImage, setDisplayImage] = useState<boolean[]>([]);

    const dataType = getDataType(heading);

    useEffect(() => {
        const list = _.get(
            data,
            `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.marketCapVacancyRentalRate.${dataType}.list`,
            []
        );
        setTableData(list);
        const reconciled = _.get(
            data,
            `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.marketCapVacancyRentalRate.${dataType}.reconciled`,
            ""
        );
        setReconciledValue(reconciled);
        setDisplayImage(Array(list.length).fill(false));
    }, [data, heading, spaceId, dataType]);

    const handleDisplayImage = (imageStatus: boolean, index: number) => {
        setDisplayImage(prevState => {
            const newState = [...prevState];
            newState[index] = imageStatus;

            return newState;
        });
    };

    const handleInputChange = (rowIndex: number, fieldKey: string, newValue: string) => {
        _.set(
            data,
            `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.marketCapVacancyRentalRate.${dataType}.list[${rowIndex}].${fieldKey}`,
            newValue
        );
        handleUpdateData(data);
    };

    const tableConfig = getTableConfig(dataType);

    const renderCell = (item: any, rowIndex: number, cellIndex: number, config: TableConfig) => {
        const { key, editable, prefix, suffix, imageObjectKey } = config;

        if (isEditing && editable && cellIndex > 1) {
            return (
                <Column width="100" spacing="100" alignmentHorizontal="end">
                    <Input
                        width={200}
                        id={`${dataType}-${rowIndex}-${cellIndex}`}
                        value={_.get(
                            data,
                            `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.marketCapVacancyRentalRate.${dataType}.list[${rowIndex}].${key}`,
                            ""
                        )}
                        suffix={suffix}
                        prefix={prefix}
                        onChange={value => handleInputChange(rowIndex, key, value)}
                        type="text"
                        placeholder="Enter value..."
                        size="small"
                    />
                </Column>
            );
        } else if (imageObjectKey === "sourceNameS3Object") {
            return (
                <>
                    <span style={{ textDecoration: "underline" }}>
                        <Link
                            onClick={() => {
                                handleDisplayImage(true, rowIndex);
                            }}
                        >
                            <Text type="b200" color="secondary">
                                {formatInputWithPrefixAndSuffix(
                                    _.get(item, `${imageObjectKey}.header`, ""),
                                    prefix,
                                    suffix
                                )}
                            </Text>
                        </Link>
                    </span>
                    <ImageSheet
                        imageHeader={_.get(item, `${imageObjectKey}.header`, "")}
                        s3ObjectIdList={_.get(item, `${imageObjectKey}.s3ObjectIdList`, [""])}
                        displayImage={displayImage[rowIndex]}
                        handleDisplayImage={handleDisplayImage}
                        index={rowIndex}
                    />
                </>
            );
        } else {
            return (
                <span>{formatInputWithPrefixAndSuffix(_.get(item, key, ""), prefix, suffix)}</span>
            );
        }
    };

    return (
        <div>
            <Row spacingInset="400">
                <Heading level={4}>{heading}</Heading>
            </Row>
            <Table headerRows={1} showDividers={true} showStripes={true} spacing={"small"}>
                <TableRow>
                    {tableConfig.map(({ header }, index) => (
                        <TableCell key={index} alignmentHorizontal={index > 1 ? "end" : "start"}>
                            {header}
                        </TableCell>
                    ))}
                </TableRow>

                {tableData.map((item, rowIndex) => (
                    <TableRow key={rowIndex}>
                        {tableConfig.map((config, cellIndex) => (
                            <TableCell
                                key={cellIndex}
                                alignmentHorizontal={cellIndex > 1 ? "end" : "start"}
                            >
                                {renderCell(item, rowIndex, cellIndex, config)}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}

                <TableRow>
                    <TableCell alignmentHorizontal="start">
                        <strong>Reconciled</strong>
                    </TableCell>
                    <TableCell alignmentHorizontal="end" />
                    <TableCell alignmentHorizontal="end" />
                    <TableCell alignmentHorizontal="end">
                        <strong>
                            {dataType === "rentalRate"
                                ? `$${reconciledValue}`
                                : `${reconciledValue}%`}
                        </strong>
                    </TableCell>
                </TableRow>
            </Table>
        </div>
    );
};
